import { useMemo, useState } from "react";
import { format } from "date-fns";
import { CheckSquare, AlertCircle } from "lucide-react";
import { Badge } from "../../../../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../components/ui/tooltip";
import EmptyTabState from "../../../../../shared/ui/EmptyTabState";
import ObjectDetailSheet from "./ObjectDetailSheet";

type Props = {
  records: any[];
  searchQuery?: string;
  statusFilter?: string;
  priorityFilter?: string;
  objectName?: string;
};

const STATUS_CLASSES: Record<string, string> = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  "in progress": "bg-indigo-50 text-indigo-700 border-indigo-200",
  in_progress: "bg-indigo-50 text-indigo-700 border-indigo-200",
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-muted text-muted-foreground",
  cancelled: "bg-red-50 text-red-700 border-red-200",
  canceled: "bg-red-50 text-red-700 border-red-200",
};

const PRIORITY_CLASSES: Record<string, string> = {
  critical: "bg-red-50 text-red-700 border-red-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  medium: "bg-yellow-50 text-yellow-700 border-yellow-200",
  normal: "bg-blue-50 text-blue-700 border-blue-200",
  low: "bg-muted text-muted-foreground",
};

function getStatusClass(status: string): string {
  return STATUS_CLASSES[status.toLowerCase()] || "bg-muted text-muted-foreground";
}

function getPriorityClass(priority: string): string {
  return PRIORITY_CLASSES[priority.toLowerCase()] || "bg-muted text-muted-foreground";
}

function safeFormat(value: any, fmt: string): string {
  if (!value) return "--";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "--";
    return format(d, fmt);
  } catch {
    return "--";
  }
}

function isOverdue(value: any, status: string): boolean {
  if (!value || ["completed", "done", "closed"].includes(status.toLowerCase())) return false;
  try {
    return new Date(value) < new Date();
  } catch {
    return false;
  }
}

function isDueSoon(value: any): boolean {
  if (!value) return false;
  try {
    const d = new Date(value);
    const diffMs = d.getTime() - Date.now();
    return diffMs > 0 && diffMs < 3 * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function getField(record: any, ...keys: string[]): string {
  for (const k of keys) {
    if (record[k]) return record[k];
  }
  return "";
}

function matchesSearch(record: any, query: string): boolean {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  const haystack = [
    record.subject, record.native_subject,
    record.body_text, record.native_body_text,
    record.status, record.native_status,
    record.priority, record.native_priority,
    record.owner_email, record.native_owner_email,
  ].filter(Boolean).join(" ").toLowerCase();
  return haystack.includes(q);
}

const TasksView = ({ records, searchQuery = "", statusFilter = "", priorityFilter = "", objectName = "Tasks" }: Props) => {
  const [selected, setSelected] = useState<any>(null);

  const filtered = useMemo(() => {
    let result = records;
    if (searchQuery.trim()) result = result.filter((r) => matchesSearch(r, searchQuery));
    if (statusFilter) {
      result = result.filter((r) =>
        (r.status || r.native_status || "").toLowerCase() === statusFilter.toLowerCase(),
      );
    }
    if (priorityFilter) {
      result = result.filter((r) =>
        (r.priority || r.native_priority || "").toLowerCase() === priorityFilter.toLowerCase(),
      );
    }
    return result;
  }, [records, searchQuery, statusFilter, priorityFilter]);

  if (filtered.length === 0) {
    return (
      <EmptyTabState
        title={`No ${objectName.toLowerCase()} yet`}
        description={searchQuery || statusFilter || priorityFilter ? "Try adjusting your filters." : `${objectName} associated with this account will appear here.`}
        icon={<CheckSquare size={24} />}
      />
    );
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Subject</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Due Date</TableHead>
            <TableHead>Owner</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.map((record: any) => {
            const id = record.id || record.external_id || record.native_external_id;
            const subject = getField(record, "subject", "native_subject") || "(Untitled)";
            const status = getField(record, "status", "native_status");
            const priority = getField(record, "priority", "native_priority");
            const dueDate = record.due_date_tz || record.native_due_date_tz;
            const ownerEmail = getField(record, "owner_email", "native_owner_email");
            const overdue = isOverdue(dueDate, status);
            const dueSoon = !overdue && isDueSoon(dueDate);

            return (
              <TableRow
                key={id || Math.random()}
                className="cursor-pointer"
                onClick={() => setSelected(record)}
              >
                <TableCell>
                  <span className="text-[13px] font-medium text-foreground">{subject}</span>
                </TableCell>
                <TableCell>
                  {status ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getStatusClass(status)}`}>
                      {status}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </TableCell>
                <TableCell>
                  {priority ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getPriorityClass(priority)}`}>
                      {priority}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </TableCell>
                <TableCell>
                  <span className={`text-[13px] inline-flex items-center gap-1 ${overdue ? "text-red-600 font-medium" : dueSoon ? "text-orange-600" : "text-muted-foreground"}`}>
                    {safeFormat(dueDate, "MMM d, yyyy")}
                    {overdue && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <AlertCircle size={13} className="text-red-500" />
                        </TooltipTrigger>
                        <TooltipContent>Overdue</TooltipContent>
                      </Tooltip>
                    )}
                  </span>
                </TableCell>
                <TableCell>
                  {ownerEmail ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="text-[13px] text-muted-foreground truncate block max-w-[150px]">
                          {ownerEmail}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{ownerEmail}</TooltipContent>
                    </Tooltip>
                  ) : (
                    <span className="text-muted-foreground">--</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {selected && (
        <ObjectDetailSheet
          open={!!selected}
          onClose={() => setSelected(null)}
          title={getField(selected, "subject", "native_subject") || "(Untitled)"}
          badge={
            getField(selected, "status", "native_status")
              ? { label: getField(selected, "status", "native_status") }
              : undefined
          }
          sourceUrl={getField(selected, "source_url", "native_source_url")}
          fields={[
            { label: "Status", value: getField(selected, "status", "native_status"), type: "badge" },
            { label: "Priority", value: getField(selected, "priority", "native_priority"), type: "badge" },
            { label: "Due Date", value: selected.due_date_tz || selected.native_due_date_tz, type: "date" },
            { label: "Owner", value: getField(selected, "owner_email", "native_owner_email") },
            { label: "Source", value: getField(selected, "source", "native_source") },
            { label: "Created", value: selected.created_at_tz || selected.native_created_at_tz || selected.created_at, type: "date" },
          ]}
          bodyMarkdown={getField(selected, "body_text", "native_body_text")}
          bodyLabel="Description"
        />
      )}
    </TooltipProvider>
  );
};

export default TasksView;
